<template>
  <card :class="[baseCss]" :rounded="rounded" :elevation="elevation">
    <card-title :color="bgColor" :color-lightness="bgColorLightness" bg-color="transparent">
      <table-layout y-align="middle" x-align="center">
        <row>
          <cell :class="['pr-1']" v-if="prevNextNavigation">
            <icon @click="setMonth('prev')" :icon-provider="iconProvider" :name="icons.navLeft" :color="bgColor" :color-lightness="bgColorLightness" :class="['cursor-pointer']" />
          </cell>
          <cell :class="['w-full', 'text-sm', 'font-semibold']">
            <table-layout v-if="yearMonthSelection">
              <row>
                <cell :class="['pr-1']">
                  <dropdown @input="onDropdownInput" :items-max-height="dropdownsHeight" :color="bgColor" :color-lightness="bgColorLightness" dense :items="monthsDropdown" v-model="month" />
                </cell>
                <cell :class="['pl-1']">
                  <dropdown @input="onDropdownInput" :items-max-height="dropdownsHeight" :color="bgColor" :color-lightness="bgColorLightness" dense :items="yearsDropdown" v-model="year" />
                </cell>
              </row>
            </table-layout>
            <div v-else :class="['truncate']">{{ months[month[0]] }} {{ year[0] }}</div>
          </cell>
          <cell :class="['pl-1']" v-if="prevNextNavigation">
            <icon @click="setMonth('next')" :icon-provider="iconProvider" :name="icons.navRight" :color="bgColor" :color-lightness="bgColorLightness" :class="['cursor-pointer']" />
          </cell>
        </row>
      </table-layout>
    </card-title>
    <card-text padding-off>
      <template v-if="showWeekdays">
        <divider />
        <ul :class="['mx-1', `${baseCss}-grid`, `${baseCss}-weekdays`, setTextColor('bgColor', 'bgColorLightness')]" v-if="weekdays.length">
          <li v-for="weekday in weekdays" :key="weekday" v-html="weekday" :title="weekday" :class="['truncate', 'font-semibold', 'text-xs', 'py-1']" @click="onWeekdayClick" />
        </ul>
      </template>
      <divider />
      <ul :class="[`${baseCss}-grid`, `${baseCss}-days`, 'text-xs', 'cursor-pointer']">
        <li v-for="day in firstDayOfMonth()" :style="{minHeight: dateMinHeight}" :key="`${day}-previous-month`" :class="[`${baseCss}-not-this-month`, 'opacity-50', 'p-1']" />
        <li v-for="day in days" :style="{minHeight: dateMinHeight}" :key="day" :class="['p-1']" @click="setDay(day)" @dblclick="onDblClick(day)">
          <div :class="[`${baseCss}-days-day`, 'transition-all', 'duration-500', day === selectedDay[0] ? [setBgColor(), setTextColor('selectedColor', 'selectedColorLightness'), 'rounded-full', selectedDay[0] !== today ? 'opacity-60' : null] : null, day === today ? [setBgColor(), setTextColor('selectedColor', 'selectedColorLightness'), 'rounded-full'] : null]" :style="{height: dateMinHeight, width: dateMinHeight, lineHeight: dateMinHeight}">{{ day }}</div>
        </li>
        <li v-for="day in 35 - days - firstDayOfMonth()" :style="{minHeight: dateMinHeight}" :key="`${day}-next-month`" :class="[`${baseCss}-not-this-month`, 'opacity-50', 'p-1']" />
      </ul>
    </card-text>
  </card>
</template>
<script>
import methods from '../../mixins/methods'

export default {
  name: 'DatetimePickerDate',
  mixins: [
    methods
  ],
  data () {
    return {
      today: null,
      year: [],
      month: [],
      monthsDropdown: [],
      yearsDropdown: [],
      selectedDay: [],
      baseCss: this.componentBaseCss('datetime-picker-date', []),
      months: Array.from({length: 12}, (item, i) => {
        return new Date(0, i).toLocaleString(this.locale, {month: this.labels})
      })
    }
  },
  computed: {
    days () {
      return this.daysInMonth()
    }
  },
  props: [
    'locale',
    'elevation',
    'dropdownsHeight',
    'selectedColor',
    'selectedColorLightness',
    'rounded',
    'showWeekdays',
    'yearMonthSelection',
    'prevNextNavigation',
    'icons',
    'date',
    'iconProvider',
    'labels',
    'weekStartsOnMonday',
    'weekdays',
    'bgColor',
    'bgColorLightness',
    'colorLightness',
    'color',
    'type',
    'dateMinHeight',
    'minDate',
    'maxDate'
  ],
  methods: {
    setDay (day) {
      this.selectedDay = [day]
      this.emit()
    },
    onDblClick (day) {
      this.selectedDay = [day]
      this.emit('datedblclick')
    },
    onWeekdayClick () {
      this.emit('dateweekdayclick')
    },
    emit (event = 'input') {
      const year = this.year[0]
      const month = this.month[0]
      const day = this.selectedDay[0]
      this.$emit(event, {year, month, day, date: new Date(`${year}-${month}-${day}`)})
    },
    onDropdownInput () {
      this.emit()
    },
    setYears () {
      // @todo handle min-date and max-date
      const maxDate = this.maxDate
      const minDate = this.minDate
      const max = maxDate ? new Date(maxDate).getUTCFullYear() : new Date().getUTCFullYear()
      const min = max - new Date(minDate || null).getUTCFullYear() + 1
      this.yearsDropdown = Array(max - (max - min)).fill('').map((v, index) => {
        const value = max - index
        return {value, text: value}
      })
    },
    processDate () {
      const date = new Date(this.date)
      this.year = [date.getFullYear()]
      this.month = [date.getMonth()]
      this.today = date.getDate()
      this.selectedDay = [this.today]
      this.monthsDropdown = this.months.map((month, index) => {
        return {
          text: month,
          value: index
        }
      })
      this.setYears()
    },
    daysInMonth (month = new Date(this.date).getMonth(), year = new Date(this.date).getFullYear()) {
      return 31 - (month - 1 ? month % 7 & 1 : year & (year % 25 ? 3 : 15) ? 3 : 2)
    },
    firstDayOfMonth () {
      const date = this.date
      const month = new Date(date).getMonth().toString()
      const year = new Date(date).getFullYear().toString()
      let day = new Date(year + '-' + month + '-01').getDay()
      return ((day === 0) ? 7 : day) + 2
    },
    setMonth (direction) {
      let currentMonth = this.month[0]
      let currentYear = this.year[0]
      direction === 'prev' ? currentMonth-- : currentMonth++
      switch (currentMonth) {
        case -1:
          currentMonth = 11
          if (direction === 'prev') {
            this.year = [currentYear - 1]
          }
          break
        case 12:
          currentMonth = 0
          if (direction === 'next') {
            this.year = [currentYear + 1]
          }
      }
      this.month = [currentMonth]
    }
  },
  mounted () {
    this.processDate()
  }
}
</script>
<style>
.bcq-datetime-picker-date .bcq-datetime-picker-date-grid
{
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
}

.bcq-datetime-picker-date .bcq-datetime-picker-date-days li
{
  display: flex;
  justify-content: center;
  align-items: center;
}

.bcq-datetime-picker-date .bcq-dropdown .bcq-line-ripple
{
  /*display: none;*/
}
</style>



// WEBPACK FOOTER //
// src/bcq-ui/components/datetime-picker-date/datetime-picker-date.vue